import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import { getAudioGraphInstance } from '../audioEngine';
import units from "../units.json"
import { addKeysAsField } from '../utils/misc';
import { SynthBassWamUI } from '../projectViews/stationViews/SynthBassWamUI.jsx';
import { sendNoteOnToWamNode, sendNoteOffToWamNode, stopAllNotesForWamNode, createWamNode} from '../utils/wam.js';
import { PianoRoller } from './PianoRoller.js';
import colors from "../../styles/colors.module.scss";

export class SynthBassWam extends InstrumentBase {

    static metadata = {
        tipus: 'synth_bass_wam',
        tipusDisplay: 'Synth Bass',
        versio: '0.1',
        color:  colors.violet,
        hasLogo: false,
        hasMiniature: false,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        pianoRollColorCode: {...PianoRoller.parametersDescription.pianoRollColorCode, options: ['melodic', 'none']},
        env_attack: {type: 'float', unit: units.second, min: 0.0, max: 2.0, initial: 0.01, bind: "preset"},
        env_decay: {type: 'float', unit: units.second, min: 0.0, max: 5.0, initial: 1.0, bind: "preset"},
        env_sustain: {type: 'float', min: 0.0, max: 1.0, initial: 0.01, bind: "preset"},
        env_release: {type: 'float', unit: units.second, min: 0.0, max: 5.0, initial: 0.6, bind: "preset"},
        osc_waveform: {type: 'enum', options: ['sine', 'triangle', 'square', 'sawtooth'], initial: 'square', bind: "preset"},
        lpf_cutoff: {type: 'float', unit: units.hertz, min: 100, max: 15000, initial: 3500, logarithmic: true, bind: "preset"},
        lpf_q: {type: 'float', min: 0.0, max: 1.0, initial: 0.7, bind: "preset"},
        lfo_rate: {type: 'float', unit: units.hertz, min: 0.1, max: 50.0, initial: 0.3, logarithmic: true, bind: "preset"},
        lfo_depth: {type: 'float', min: 0.0, max: 2000.0, initial: 0.0, bind: "preset"},
        osc_octave: {type: 'int', min: -2, max: 2, step: 1, initial: 0, bind: "preset"},
        fx_distortion_amount: {type: 'float', min: 0.0, max: 100.0, initial: 0.0, bind: "preset"},
        osc_portamento: {type: 'float', unit: units.second, min: 0.0, max: 2.0, initial: 0.01, logarithmic: true, bind: "preset"},
        osc_detune: {type: 'float', unit: units.cents, min: -10.0, max: 10.0, initial: 0.8, bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        usable: {min: 24, max: 47}
    }

    getUserInterfaceComponent() {
        return SynthBassWamUI
    }

    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();
        const wamUrl = appPrefix + '/static/dist/public/wam/MonoSynth/index.js'
        const wamNode = await createWamNode(wamUrl, this.id);
        //wamNode.getParameterInfo().then((params) => console.debug(params));  // Print available WAM node parameters

        const gainOutput = new Tone.Gain(Tone.dbToGain(-12));
        this.audioNodes = {
            gainOutput: gainOutput,
            wamNode: wamNode,
        }
        Tone.connect(this.audioNodes.wamNode, this.audioNodes.gainOutput);

        // Seteja gain general
        this.audioNodes.wamNode.setParameterValues({
            'volume': {value: -3.0},
            'resonance': {value: 0.2},
        })

        // Crea els nodes d'efectes (això també els afegirà al diccionari de nodes de l'instrument)
        this.addEffectChainNodes(this.audioNodes.gainOutput, this.bus);
    }

    setParameterInAudioGraph(name, value) {
        switch (name) {
            case "lpf_cutoff":
                this.audioNodes.wamNode.setParameterValues({
                    'cutoff': {value: value},
                })
                break;
            case "lpf_q":
                this.audioNodes.wamNode.setParameterValues({
                    'resonance': {value: value},
                })
                break;
            case "lfo_rate":
                this.audioNodes.wamNode.setParameterValues({
                    'lfoRate': {value: value},
                })
                break;
            case "lfo_depth":
                this.audioNodes.wamNode.setParameterValues({
                    'lfoAmount': {value: value},
                })
                break;
            case "fx_distortion_amount":
                this.audioNodes.wamNode.setParameterValues({
                    'distortionAmount': {value: value},
                })
                break;
            case "osc_octave":
                this.audioNodes.wamNode.setParameterValues({
                    'octave': {value: value},
                })
                break;
            case "osc_portamento":
                this.audioNodes.wamNode.setParameterValues({
                    'glideTime': {value: value},
                })
                break;
            case "osc_detune":
                this.audioNodes.wamNode.setParameterValues({
                    'detune': {value: value},
                })
                break;
            case "env_attack":
            case "env_decay":
            case "env_sustain":
            case "env_release":
                this.audioNodes.wamNode.setParameterValues({
                    [name.replace("env_", "")]: {value: value},
                })
                break;
            case "osc_waveform":
                this.audioNodes.wamNode.setParameterValues({
                    'waveform': { value: {
                        sawtooth: 3, 
                        triangle: 1, 
                        square: 2, 
                        sine: 0}[value] },
                })
                break;
        }
    }

    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        if (!Object.keys(this.audioNodes).length) { return };
        
        // Iterate over all the notes in the sequence and trigger those that start in the current beat (step)
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        const sequence = this.getPlaybackClip().sequence;
        for (let i = 0; i < sequence.length; i++) {
            const note = sequence[i];
            if (note.onset === currentStep) {
                const duration = Math.min(note.onset + note.duration, this.getPlaybackClip().length) - note.onset;
                sendNoteOnToWamNode(this.audioNodes.wamNode, note.pitch, (note.velocity ?? 127), time);
                sendNoteOffToWamNode(this.audioNodes.wamNode, note.pitch, 0, time + duration * getAudioGraphInstance().get16BeatTime());
            }
        }   
    }
    
    onTransportStop() {
        if (!Object.keys(this.audioNodes).length) { return };
        stopAllNotesForWamNode(this.audioNodes.wamNode, Tone.getContext().currentTime);
    }
    
    onStopAllSounds() {
        if (!Object.keys(this.audioNodes).length) { return };
        stopAllNotesForWamNode(this.audioNodes.wamNode, Tone.getContext().currentTime);
    }
    
    onMidiNote(pitch, midiVelocity=127, noteOff, extras) {
        if (!getAudioGraphInstance().isGraphBuilt()) return; 
        if (!Object.keys(this.audioNodes).length) { return };
        if (!noteOff) {
            sendNoteOnToWamNode(this.audioNodes.wamNode, pitch, midiVelocity, Tone.getContext().currentTime);
        } else {
            sendNoteOffToWamNode(this.audioNodes.wamNode, pitch, midiVelocity, Tone.getContext().currentTime);
        }
        if (!extras.skipRecording) this.handlePianoRollRecording(pitch, midiVelocity, noteOff);
    }
}